<template>
  <TheModal :show="show" size="xxxl" @close="onClose">
    <div v-if="payload" class="landing-demo-tender-details">
      <div class="tender-details-modal">
        <aside class="tender-details-sidebar" aria-label="Разделы карточки тендера">
          <button
            v-for="tab in sidebarTabs"
            :key="tab.key"
            type="button"
            class="tender-details-tab"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </button>
          <button
            type="button"
            class="landing-access-cta landing-access-cta--sidebar"
            @click="onGetAccess"
          >
            Получить доступ
          </button>
        </aside>

        <section
          class="tender-details-main"
          :class="{ 'tender-details-main--ask-ai': activeTab === 'ask-ai' }"
        >
          <div v-if="activeTab === 'overview'" class="tender-details-section">
            <div class="tender-details-header">
              <h5 class="tender-details-title">{{ payload.row.title }}</h5>
              <span class="badge bg-success">Демонстрационный анализ завершён</span>
            </div>
            <p class="landing-demo-readonly-note">
              Демонстрационная карточка. Полный доступ — после регистрации.
            </p>
            <div class="tender-overview-grid">
              <div
                v-for="(entry, idx) in payload.overviewEntries"
                :key="idx"
                class="tender-overview-field"
              >
                <span class="tender-overview-label">{{ entry.label }}</span>
                <span class="tender-overview-value">{{ entry.value }}</span>
              </div>
            </div>
            <div class="tender-winprob-overview card border-0 shadow-sm">
              <div class="card-body">
                <div class="d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h6 class="mb-0">Вероятность победы</h6>
                  <span class="badge px-3 py-2" :class="winProbabilityBadge">{{ winProbabilityDisplay }}</span>
                </div>
                <p v-if="payload.analysis?.win_short_comment" class="mb-0">
                  <strong>Короткий вывод:</strong> {{ payload.analysis.win_short_comment }}
                </p>
              </div>
            </div>
            <div v-if="payload.attachments?.length" class="tender-overview-docs">
              <h6 class="tender-overview-docs__title">Документы</h6>
              <ul class="tender-details-list">
                <li v-for="(file, idx) in payload.attachments" :key="idx">{{ file.name }}</li>
              </ul>
            </div>
          </div>

          <div v-else-if="activeTab === 'criteria'" class="tender-details-section td-panel">
            <header class="td-panel__header">
              <h6 class="mb-0">Критерии конкурса</h6>
            </header>
            <p class="landing-demo-readonly-note">Демо-данные. Редактирование доступно в системе.</p>
            <div class="table-responsive">
              <table class="table table-sm align-middle td-panel__table">
                <thead class="table-light">
                  <tr>
                    <th style="width: 70px" class="text-center">№</th>
                    <th>Наименование</th>
                    <th style="width: 120px" class="text-end">Вес</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(crit, idx) in payload.criteriaRows" :key="idx">
                    <td class="text-center">{{ crit.number }}</td>
                    <td>
                      <div class="d-flex align-items-start gap-2">
                        <span class="td-panel__indent" :style="{ width: (crit.level || 0) * 16 + 'px' }" />
                        <div>
                          <strong>{{ crit.name }}</strong>
                          <div v-if="crit.description" class="text-muted small">{{ crit.description }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="text-end">{{ crit.weight || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else-if="activeTab === 'products'" class="tender-details-section td-panel">
            <header class="td-panel__header">
              <h6 class="mb-0">Список товаров</h6>
            </header>
            <div class="table-responsive">
              <table class="table table-sm align-middle td-panel__table">
                <thead class="table-light">
                  <tr>
                    <th style="width: 60px">№</th>
                    <th>Наименование</th>
                    <th>Описание</th>
                    <th class="text-end">Кол-во</th>
                    <th>Ед.</th>
                    <th class="text-end">Цена (НМЦК)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in payload.products" :key="idx">
                    <td>{{ idx + 1 }}</td>
                    <td class="fw-semibold">{{ item.name }}</td>
                    <td>{{ item.description || '—' }}</td>
                    <td class="text-end">{{ item.quantity ?? '—' }}</td>
                    <td>{{ item.unit || '—' }}</td>
                    <td class="text-end">{{ money(item.price_from_docs) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else-if="activeTab === 'win-probability'" class="tender-details-section">
            <h5 class="tender-details-title">Вероятность победы</h5>
            <div class="tender-probability">
              <div class="tender-probability__value">
                <span class="tender-probability__label">Оценка искусственного интеллекта</span>
                <span class="badge px-3 py-2 tender-probability__score" :class="winProbabilityBadge">
                  {{ winProbabilityDisplay }}
                </span>
              </div>
              <div class="tender-probability__details">
                <p v-if="payload.analysis?.win_short_comment" class="mb-2">
                  <strong>Короткий вывод:</strong> {{ payload.analysis.win_short_comment }}
                </p>
                <p v-if="payload.analysis?.win_reasoning" class="mb-0"><strong>Обоснование:</strong></p>
                <pre v-if="payload.analysis?.win_reasoning" class="tender-details-pre">{{
                  payload.analysis.win_reasoning
                }}</pre>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'compliance'" class="tender-details-section td-panel">
            <header class="td-panel__header">
              <div>
                <h6>Риски закупки (44/223)</h6>
                <small class="text-muted">Правила + ИИ; не является юридическим заключением.</small>
              </div>
            </header>
            <div class="td-panel__body">
              <div class="d-flex flex-wrap align-items-center gap-3">
                <span class="badge bg-success">Готово</span>
                <span class="fw-semibold">Балл риска: {{ payload.compliance.scan.risk_score }}</span>
                <span class="text-muted small">Закон: {{ payload.compliance.scan.law_basis }}</span>
              </div>
              <p class="small mb-0">{{ payload.compliance.scan.summary }}</p>
              <div class="table-responsive">
                <table class="table table-sm align-middle td-panel__table">
                  <thead class="table-light">
                    <tr>
                      <th>Серьёзность</th>
                      <th>Заголовок</th>
                      <th>Норма</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="f in payload.compliance.findings" :key="f.id">
                      <td>
                        <span class="badge" :class="severityClass(f.severity)">{{
                          f.severityLabel || formatSeverity(f.severity)
                        }}</span>
                      </td>
                      <td>
                        <div class="fw-semibold">{{ f.title }}</div>
                        <div v-if="f.recommendation" class="small mt-1">
                          <strong>Рекомендация:</strong> {{ f.recommendation }}
                        </div>
                      </td>
                      <td class="small">{{ f.law_ref || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'roadmap'" class="tender-details-section td-panel">
            <header class="td-panel__header">
              <div>
                <h6>Дорожная карта согласования</h6>
                <small class="text-muted">Чеклист по документам</small>
              </div>
            </header>
            <div class="table-responsive">
              <table class="table table-sm align-middle td-panel__table">
                <thead class="table-light">
                  <tr>
                    <th>Шаг</th>
                    <th style="width: 140px">Срок</th>
                    <th style="width: 100px">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="t in payload.roadmapTasks"
                    :key="t.id"
                    :class="{ 'table-danger': isRoadmapOverdue(t) }"
                  >
                    <td>
                      <div class="fw-semibold">{{ t.title }}</div>
                      <div v-if="t.description" class="text-muted small">{{ t.description }}</div>
                    </td>
                    <td>{{ formatRoadmapDate(t.due_at) }}</td>
                    <td>{{ t.status === 'done' ? 'Готово' : 'К выполнению' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else-if="activeTab === 'ask-ai'" class="tender-details-section tender-details-section--ask-ai">
            <h5 class="tender-details-title">Спросить у ИИ</h5>
            <div class="tender-ai-chat">
              <div class="tender-ai-chat__history">
                <div class="tender-ai-chat__messages">
                  <div
                    v-for="msg in formattedChat"
                    :key="msg.id"
                    :class="['tender-ai-chat__message', `tender-ai-chat__message--${msg.role}`]"
                  >
                    <div class="tender-ai-chat__message-meta">
                      <span>{{ msg.roleLabel }}</span>
                      <time>{{ msg.time }}</time>
                    </div>
                    <pre class="tender-ai-chat__message-text">{{ msg.content }}</pre>
                  </div>
                </div>
              </div>
              <div class="tender-ai-chat__composer">
                <textarea
                  class="form-control"
                  rows="4"
                  disabled
                  placeholder="В демо отправка недоступна. Получите доступ для чата с документами."
                />
                <div class="d-flex align-items-center gap-3 mt-2">
                  <small class="text-muted flex-grow-1">Ответы формируются по документам тендера в полной версии.</small>
                  <button type="button" class="landing-access-cta" @click="onGetAccess">
                    Получить доступ
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </TheModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

import TheModal from '@/components/TheModal.vue'
import '@/components/tender-details/tender-panel.css'
import '@/styles/landing-access-cta.css'
import './landingDemoTenderDetails.css'

const props = defineProps({
  show: { type: Boolean, default: false },
  payload: { type: Object, default: null },
})

const emit = defineEmits(['close', 'open-quiz'])

const sidebarTabs = [
  { key: 'overview', label: 'Основная информация' },
  { key: 'criteria', label: 'Критерии конкурса' },
  { key: 'products', label: 'Список товаров' },
  { key: 'win-probability', label: 'Вероятность победы' },
  { key: 'compliance', label: 'Риски закупки' },
  { key: 'roadmap', label: 'Дорожная карта' },
  { key: 'ask-ai', label: 'Спросить у ИИ' },
]

const activeTab = ref('overview')

watch(
  () => props.show,
  (visible) => {
    if (visible) activeTab.value = 'overview'
  },
)

const winProbabilityDisplay = computed(() => {
  const p = Number(props.payload?.analysis?.win_probability_percent)
  return Number.isFinite(p) ? `${p}%` : '—'
})

const winProbabilityBadge = computed(() => {
  const p = Number(props.payload?.analysis?.win_probability_percent)
  if (!Number.isFinite(p)) return ''
  if (p >= 70) return 'bg-success'
  if (p >= 40) return 'bg-warning text-dark'
  return 'bg-danger'
})

const formattedChat = computed(() => {
  const messages = props.payload?.chatMessages || []
  return messages.map((msg) => ({
    id: msg.id,
    role: msg.role,
    roleLabel: msg.role === 'user' ? 'Вы' : 'Ассистент',
    content: msg.content,
    time: msg.created_at
      ? new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(msg.created_at))
      : '',
  }))
})

function severityClass(severity) {
  const s = String(severity || '').toLowerCase()
  if (s === 'critical' || s === 'high') return 'bg-danger'
  if (s === 'medium') return 'bg-warning text-dark'
  return 'bg-secondary'
}

function formatSeverity(severity) {
  const map = {
    critical: 'критический',
    high: 'высокий',
    medium: 'средний',
    low: 'низкий',
  }
  return map[String(severity || '').toLowerCase()] || severity || '—'
}

function isRoadmapOverdue(task) {
  if (task.status === 'done') return false
  if (!task.due_at) return false
  return new Date(task.due_at) < new Date()
}

function formatRoadmapDate(iso) {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('ru-RU').format(new Date(iso))
  } catch {
    return iso
  }
}

function money(value) {
  if (value == null || value === '') return '—'
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value) + ' ₽'
}

function onClose() {
  emit('close')
}

function onGetAccess() {
  emit('close')
  emit('open-quiz')
}
</script>
